import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    CraftDetailsDto,
    CraftService,
    CreateCraftCommand,
    CreateRpaTypeCommand,
    FlyFreelyError,
    FlyFreelyLoggingService,
    hasFeatureFlag,
    NotFound,
    Organisation,
    PROPULSION_SYSTEMS,
    ResourceTypeComponentsService,
    RpaTypeDto,
    RpaTypesService,
    RPA_CATEGORIES,
    toLocalDate,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { preHide } from '@flyfreely-portal-ui/ngx-bootstrap-customisation';
import { rpaTypeSearch } from '@flyfreely-portal-ui/ui';
import {
    collapseOnLeaveAnimation,
    expandOnEnterAnimation
} from 'angular-animations';
import { BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import { BehaviorSubject, EMPTY, of, Subject } from 'rxjs';
import { catchError, filter, take, takeUntil } from 'rxjs/operators';
import { compareRpaType } from '../helpers';
import { RpaUploadService } from '../rpa-upload.service';

const INITIAL_RPA_TYPE_LIST = [] as RpaTypeDto[];

@Component({
    selector: 'rpa-add-dialogue',
    templateUrl: './rpa-add-dialogue.component.html',
    providers: [RpaUploadService],
    animations: [collapseOnLeaveAnimation(), expandOnEnterAnimation()]
})
export class RpaAddDialogue implements OnInit, OnDestroy {
    @Input()
    rpaTypes?: RpaTypeDto[];
    @Input() organisation: Organisation;

    /**
     * Defaults that can be used to prepopulate an RPA
     */
    @Input() defaults?: {
        nickname?: string;
        rpaTypeId?: number;
        /**
         * The make/model identifiers that can be used to find an RPA Type
         */
        rpaType?: {
            makeIdentifier: string;
            modelIdentifier: string;
        };
    };

    @Output() created = new EventEmitter<CraftDetailsDto>();

    rpaNewForm = new FormGroup({
        nickname: new FormControl('', [Validators.required]),
        isDummy: new FormControl(false),
        nfcUid: new FormControl<string>(undefined),
        callSign: new FormControl<string>(undefined),
        manufacturerSerialNumber: new FormControl<string>(undefined),
        maximumVisualDistance: new FormControl<number>(undefined),
        rpaTypeId: new FormControl(undefined, Validators.required),
        make: new FormControl<string>(undefined),
        makeText: new FormControl<string>(undefined),
        modelText: new FormControl<string>(undefined),
        rpaType: new FormControl<CreateRpaTypeCommand.CraftType>(undefined),
        propulsion: new FormControl<CreateRpaTypeCommand.PropulsionSystem>(
            undefined
        ),
        initialFlightTime: new FormControl(0),
        procurementDate: new FormControl<Date>(undefined),
        components: new FormArray([])
    });
    working = false;
    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    canUseNfc: boolean;
    canBulkUpload = false;
    rpaTypeList$ = new BehaviorSubject<RpaTypeDto[]>(INITIAL_RPA_TYPE_LIST);
    addMakeModel = false;
    rpaMakes: string[];
    rpaModels: string[];

    rpaCategories = RPA_CATEGORIES;
    propulsionSystems = PROPULSION_SYSTEMS;

    makeListed = false;
    hasComponentsSetUp = false;
    showComponents = true;

    makeValidator = 'make';
    modelValidator = 'model';

    makeLocked = false;
    modelLocked = false;
    typeLocked = false;

    rpaTypeSearch = rpaTypeSearch;

    constructor(
        private modal: BsModalRef<RpaAddDialogue>,
        modalOptions: ModalOptions,
        private craftService: CraftService,
        private rpaTypesService: RpaTypesService,
        private commonDialoguesService: CommonDialoguesService,
        private rpaUploadService: RpaUploadService,
        private resourceTypeComponentsService: ResourceTypeComponentsService,
        private logging: FlyFreelyLoggingService,
        private changeDetector: ChangeDetectorRef
    ) {
        modalOptions.closeInterceptor = () =>
            preHide(this.rpaNewForm, this.commonDialoguesService);

        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        this.setupPermissions();

        this.setupRpaTypeList();

        this.setupFormSubscriptions();

        this.setupDefaultValueHandler();
    }

    private setupRpaTypeList() {
        (this.rpaTypes != null
            ? of(this.rpaTypes)
            : this.rpaTypesService
                  .findRpaTypes(this.organisation.id)
                  .pipe(takeUntil(this.ngUnsubscribe$))
        ).subscribe(rpaTypeList => {
            this.refreshMakesModels(rpaTypeList);

            const rpaModels = rpaTypeList.concat([
                {
                    id: -1,
                    make: '',
                    model: 'Not Listed',
                    rpaCategory: null,
                    compatibleBatteryTypeIdList: [],
                    compatibleBatteryTypeRequirements: {}
                }
            ]);
            rpaModels.sort(compareRpaType);

            this.rpaTypeList$.next(rpaModels);
        });
    }

    private setupFormSubscriptions() {
        this.rpaNewForm.controls.rpaTypeId.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(val => {
                if (val === -1) {
                    this.addMakeModel = true;
                    this.rpaNewForm.controls.make.setValidators(
                        Validators.required
                    );
                    this.rpaNewForm.controls.modelText.setValidators(
                        Validators.required
                    );
                    this.rpaNewForm.controls.rpaType.setValidators(
                        Validators.required
                    );
                    this.rpaNewForm.controls.propulsion.setValidators(
                        Validators.required
                    );
                } else {
                    this.addMakeModel = false;
                    this.rpaNewForm.controls.make.clearValidators();
                    this.rpaNewForm.controls.modelText.clearValidators();
                    this.rpaNewForm.controls.rpaType.clearValidators();
                    this.rpaNewForm.controls.propulsion.clearValidators();
                }

                this.rpaNewForm.updateValueAndValidity();
                this.changeDetector.detectChanges();
            });

        this.rpaNewForm.controls.make.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(val => {
                this.onMakeUpdated(val);
            });

        this.rpaNewForm.controls.rpaTypeId.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(val => {
                const components = this.rpaNewForm.controls
                    .components as FormArray;
                while (components.controls.length > 0) {
                    components.removeAt(0);
                }
                if (val != null && val !== -1) {
                    this.findComponentsForType(val);
                } else {
                    this.hasComponentsSetUp = false;
                }
            });
    }

    private setupPermissions() {
        this.canUseNfc = hasFeatureFlag(this.organisation, 'craft-nfc');
        this.canBulkUpload = hasFeatureFlag(
            this.organisation,
            'organisationBulkSetup'
        );
    }

    private setupDefaultValueHandler() {
        if (this.defaults == null) {
            return;
        }

        this.rpaNewForm.patchValue({
            nickname: this.defaults.nickname,
            rpaTypeId: this.defaults.rpaTypeId
        });

        if (this.defaults.rpaType != null) {
            this.rpaTypeList$
                .pipe(
                    filter(types => types !== INITIAL_RPA_TYPE_LIST),
                    take(1),
                    takeUntil(this.ngUnsubscribe$)
                )
                .subscribe(rpaTypes => {
                    const rpaType = rpaTypes.find(
                        t =>
                            t.makeIdentifier ===
                                this.defaults.rpaType.makeIdentifier &&
                            t.modelIdentifier ===
                                this.defaults.rpaType.modelIdentifier
                    );
                    if (rpaType != null) {
                        this.rpaNewForm.patchValue({ rpaTypeId: rpaType.id });
                    }
                });
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private refreshMakesModels(rpaTypeList: RpaTypeDto[]) {
        const { uniqueMakeList, uniqueModelList } = rpaTypeList.reduce(
            (acc, t) => ({
                uniqueMakeList:
                    acc.uniqueMakeList.indexOf(t.make) !== -1 || t.make === ''
                        ? acc.uniqueMakeList
                        : acc.uniqueMakeList.concat(t.make),
                uniqueModelList:
                    acc.uniqueModelList.indexOf(t.model) !== -1 ||
                    t.model === ''
                        ? acc.uniqueModelList
                        : acc.uniqueModelList.concat(t.model)
            }),
            { uniqueMakeList: [] as string[], uniqueModelList: [] as string[] }
        );

        this.rpaMakes = ['Not Listed'].concat(uniqueMakeList);
        this.rpaModels = ['Not Listed'].concat(uniqueModelList);
        this.makeListed = true;
    }

    private onMakeUpdated(val: string) {
        if (val === 'Not Listed') {
            this.makeListed = false;
            this.makeValidator = 'makeText';
            this.rpaNewForm.controls.makeText.setValidators(
                Validators.required
            );
            this.rpaNewForm.controls.make.clearValidators();
        } else {
            this.makeListed = true;
            this.makeValidator = 'make';
            this.rpaNewForm.controls.makeText.clearValidators();
            this.rpaNewForm.controls.make.setValidators(Validators.required);
        }

        this.rpaNewForm.updateValueAndValidity();
        this.changeDetector.detectChanges();
    }

    findComponentsForType(typeId: number) {
        this.resourceTypeComponentsService
            .findResourceSchematicComponents('CRAFT', typeId)
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                catchError((error: FlyFreelyError) => {
                    if (error instanceof NotFound) {
                        this.hasComponentsSetUp = false;
                    }
                    return EMPTY;
                })
            )
            .subscribe(result => {
                result.components.sort((a, b) => {
                    if (a.componentType.name < b.componentType.name) {
                        return -1;
                    } else if (a.componentType.name > b.componentType.name) {
                        return 1;
                    } else {
                        return 0;
                    }
                });
                result.components.forEach(component => {
                    const name = `${
                        component.componentGroup?.name ??
                        component.componentType.name
                    } ${component.designation}`;
                    const icon =
                        component.componentType.icon != null &&
                        component.componentType.iconFamily != null &&
                        component.componentType.iconFamily === 'FONT_AWESOME'
                            ? `fal fa-${component.componentType.icon}`
                            : null;
                    const group = new FormGroup({
                        name: new FormControl(undefined),
                        id: new FormControl(undefined),
                        icon: new FormControl(undefined),
                        serialNumber: new FormControl(undefined),
                        firmwareVersion: new FormControl(undefined),
                        hasSerialNumber: new FormControl(undefined),
                        hasFirmware: new FormControl(undefined)
                    });
                    group.patchValue({
                        name: name,
                        id: component.id,
                        icon: icon,
                        serialNumber: '',
                        firmwareVersion: '',
                        hasSerialNumber: component.hasSerialNumber,
                        hasFirmware: component.hasFirmwareVersion
                    });
                    (this.rpaNewForm.controls.components as FormArray).push(
                        group
                    );
                });

                this.hasComponentsSetUp = true;
            })
            .add(this.workTracker.createTracker());
    }

    save(addAnother: boolean) {
        const value = this.rpaNewForm.value;
        const make = this.makeListed === true ? value.make : value.makeText;
        const model = value.modelText;
        if (value.rpaTypeId == null) {
            const existing = this.rpaTypeList$
                .getValue()
                .findIndex(m => m.make === make && m.model === model);

            if (existing !== -1) {
                value.rpaTypeId = this.rpaTypeList$[existing].id;
            }
        }
        const initialComponents =
            value.components != null && value.components.length > 0
                ? value.components.map((component: any) => ({
                      componentId: component.id,
                      serialNumber:
                          component.serialNumber.length > 0
                              ? component.serialNumber
                              : null,
                      firmwareVersion:
                          component.firmwareVersion.length > 0
                              ? component.firmwareVersion
                              : null
                  }))
                : [];
        const forSaving: CreateCraftCommand = {
            organisationId: this.organisation.id,
            nickname: value.nickname,
            nfcUid: value.nfcUid,
            procurementDate: toLocalDate(value.procurementDate),
            initialFlightTime: value.initialFlightTime
                ? value.initialFlightTime
                : 0,
            rpaTypeId: value.rpaTypeId != null ? value.rpaTypeId : null,
            manufacturerSerialNumber: value.manufacturerSerialNumber,
            callSign: value.callSign,
            isDummy: value.isDummy || false,
            maximumVisualDistance: value.maximumVisualDistance,
            initialComponentList: initialComponents
        };
        if (value.rpaTypeId !== -1) {
            this.createRpa(forSaving, addAnother).add(
                this.workTracker.createTracker()
            );
        } else {
            const command: CreateRpaTypeCommand = {
                make: make,
                model: model,
                organisationId: this.organisation.id,
                physicalSpecifications: {},
                performanceSpecifications: {},
                otherSpecifications: {},
                propulsionSystem: value.propulsion,
                craftType: value.rpaType
            };
            this.rpaTypesService
                .createRpaType(command)
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(type => {
                    forSaving.rpaTypeId = type.id;
                    this.createRpa(forSaving, addAnother).add(
                        this.workTracker.createTracker()
                    );
                })
                .add(this.workTracker.createTracker());
        }
    }

    createRpa(forSaving: CreateCraftCommand, addAnother: boolean) {
        return this.craftService
            .createCraft(forSaving)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: savedCraft => {
                    this.logging.success('RPA created');
                    this.created.next(savedCraft);
                    this.rpaNewForm.reset();
                    this.rpaNewForm.markAsPristine();
                    if (!addAnother) {
                        this.modal.hide();
                    }
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while creating RPA: ${error.message}`
                    );
                }
            });
    }

    invalidFileSelection(files: File[]) {
        if (files == null) {
            return;
        }
        this.logging.error(null, 'This file type is not supported');
    }

    bulkUpload() {
        this.rpaUploadService.showBulkUpload(this.organisation.id);

        this.rpaUploadService.doneImporting$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.modal.hide());
    }

    cancel() {
        this.modal.hide();
    }

    get nickname() {
        return this.rpaNewForm.get('nickname');
    }

    get isDummy() {
        return this.rpaNewForm.get('isDummy');
    }

    get nfcUid() {
        return this.rpaNewForm.get('nfcUid');
    }

    get callSign() {
        return this.rpaNewForm.get('callSign');
    }

    get manufacturerSerialNumber() {
        return this.rpaNewForm.get('manufacturerSerialNumber');
    }

    get maximumVisualDistance() {
        return this.rpaNewForm.get('maximumVisualDistance');
    }

    get rpaTypeId() {
        return this.rpaNewForm.get('rpaTypeId');
    }

    get procurementDate() {
        return this.rpaNewForm.get('procurementDate');
    }

    get initialFlightTime() {
        return this.rpaNewForm.get('initialFlightTime');
    }

    get components() {
        return this.rpaNewForm.get('components') as FormArray;
    }
}
